import { Plural, Trans } from '@lingui/react/macro';

import Message from '@/design_system/Message';
import Stack from '@/design_system/Stack';
import Paragraph from '@/design_system/Typography/Paragraph';
import {
  RequestWithRelationsFromRequestsList,
  useBulkSubscribeRequests,
  useBulkUnsubscribeRequests,
} from '@/models/request';

import { BulkConfirmationDialog } from './BulkConfirmationDialog';

export const BulkFollowDialog = ({
  showDialog,
  setShowDialog,
  selectedRequests,
  eligibleRequests,
  shouldUnfollow,
  onSuccess,
}: {
  showDialog: boolean;
  setShowDialog: (show: boolean) => void;
  selectedRequests: RequestWithRelationsFromRequestsList[];
  eligibleRequests: RequestWithRelationsFromRequestsList[];
  shouldUnfollow: boolean;
  onSuccess: () => void;
}) => {
  const { mutateAsync: subscribeRequests, isPending: isSubscribePending } =
    useBulkSubscribeRequests();
  const { mutateAsync: unsubscribeRequests, isPending: isUnsubscribePending } =
    useBulkUnsubscribeRequests();

  const isPending = isSubscribePending || isUnsubscribePending;
  const eligibleCount = eligibleRequests.length;
  const skippedCount = selectedRequests.length - eligibleCount;

  return (
    <BulkConfirmationDialog
      title={
        shouldUnfollow ? (
          <Trans id="requests.bulk.unsubscribe.title">Unsubscribe from requests</Trans>
        ) : (
          <Trans id="requests.bulk.subscribe.title">Subscribe to requests</Trans>
        )
      }
      confirm={
        shouldUnfollow ? (
          <Trans id="requests.bulk.unsubscribe.confirm">Unsubscribe</Trans>
        ) : (
          <Trans id="requests.bulk.subscribe.confirm">Subscribe</Trans>
        )
      }
      showDialog={showDialog}
      setShowDialog={setShowDialog}
      isPending={isPending}
      isConfirmDisabled={eligibleCount === 0}
      requests={selectedRequests}
      onCloseAfterResult={onSuccess}
      onConfirm={() => {
        const requestIds = eligibleRequests.map((request) => request.id);

        if (shouldUnfollow) {
          return unsubscribeRequests({ requestIds });
        }

        return subscribeRequests({ requestIds });
      }}
    >
      <Stack gap="0.5rem">
        <Paragraph>
          {shouldUnfollow ? (
            <Plural
              id="requests.bulk.unsubscribe.scope"
              value={eligibleCount}
              one="You'll unsubscribe from # request"
              other="You'll unsubscribe from # requests"
            />
          ) : (
            <Plural
              id="requests.bulk.subscribe.scope"
              value={eligibleCount}
              one="You'll subscribe to # request"
              other="You'll subscribe to # requests"
            />
          )}
        </Paragraph>
        {/* Unsubscribe is only offered when every selected request is subscribed, so nothing is skipped */}
        {skippedCount > 0 && (
          <Message type="neutral">
            <Plural
              id="requests.bulk.subscribe.already"
              value={skippedCount}
              one="You're already subscribed to # request"
              other="You're already subscribed to # requests"
            />
          </Message>
        )}
      </Stack>
    </BulkConfirmationDialog>
  );
};
